import React from 'react';
import ReactModal from 'react-modal';
import closeSvg from 'src/assets/close.svg';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { IBenefitsVersionManagement } from 'src/interfaces';
import { CommonInput, DatePicker, RegularButton, CommonCurrencyInput } from 'pg-ui-library';

import {
  CloseIcon,
  BenefitFormTitle,
  BenefitFormContent,
  FormTitle,
  BenefitVersionFormContainer,
  FormItem,
  SubmitContainer,
} from './styles';
import {
  useBenefitVersion,
  useBenefitVersionEditModalIsOpen,
  useSetBenefitVersionEditModal,
} from '../benefits-version-management';
import { useEditBenefitVersion } from './mutations/edit-benefit-version.mutation';

export const BenefitVersionForm: React.FC<{ bVersion: IBenefitsVersionManagement }> = ({ bVersion }) => {
  const { t } = useTranslation();
  const editMutation = useEditBenefitVersion();
  const formik = useFormik<IBenefitsVersionManagement>({
    initialValues: {
      id: bVersion.id,
      benefitId: bVersion.benefitId,
      effectiveDate: bVersion.effectiveDate,
      expiration: bVersion.expiration,
      plan: bVersion.plan,
      amountOrRate: bVersion.amountOrRate,
    },
    onSubmit: values =>
      editMutation.mutate({
        ...values,
        amountOrRate: Number(values.amountOrRate),
        effectiveDate: new Date(values.effectiveDate).toISOString(),
        expiration: new Date(values.expiration).toISOString(),
      }),
  });

  return (
    <BenefitVersionFormContainer>
      <FormItem style={{ flex: '0 0 calc(50% - 20px)' }}>
        <CommonInput
          name="benefitId"
          label="Benefit ID"
          value={formik.values.benefitId}
          disabled
          readOnly
          style={{ width: '100%' }}
        />
      </FormItem>
      <FormItem style={{ flex: '0 0 calc(50% - 20px)' }}>
        <CommonInput name="plan" label="Plan" value={formik.values.plan} disabled readOnly style={{ width: '100%' }} />
      </FormItem>
      <FormItem>
        <DatePicker
          id="effectiveDate"
          name="effectiveDate"
          label="Effective Start"
          value={formik.values.effectiveDate}
          dateFormat="MM/dd/yyyy"
          onChange={(val: Date) => {
            formik.setFieldValue('effectiveDate', val.toLocaleDateString());
          }}
        />
      </FormItem>
      <FormItem>
        <DatePicker
          id="expiration"
          name="expiration"
          label="Expiration"
          dateFormat="MM/dd/yyyy"
          value={formik.values.expiration}
          onChange={(val: Date) => {
            formik.setFieldValue('expiration', val.toLocaleDateString());
          }}
        />
      </FormItem>
      <FormItem>
        <CommonCurrencyInput
          id="amountOrRate"
          label="Rate/Amount"
          name="amountOrRate"
          placeholder="Please enter a number"
          prefix="$"
          decimalsLimit={2}
          allowNegativeValue={false}
          value={formik.values.amountOrRate}
          onValueChange={val => formik.setFieldValue('amountOrRate', val)}
        />
      </FormItem>
      <SubmitContainer>
        <RegularButton style={{ margin: '0 3px 0 0' }} type="submit" onClick={() => formik.handleSubmit()}>
          {t('global.form.save')}
        </RegularButton>
      </SubmitContainer>
    </BenefitVersionFormContainer>
  );
};

export const BenefitVersionModal: React.FC = () => {
  const { t } = useTranslation();
  const isOpen = useBenefitVersionEditModalIsOpen();
  const setIsOpen = useSetBenefitVersionEditModal();

  const selectedBenefitVersion = useBenefitVersion();

  const handleModal = () => {
    setIsOpen(!isOpen);
  };

  return (
    <ReactModal
      isOpen={isOpen}
      ariaHideApp={false}
      onRequestClose={handleModal}
      style={{
        overlay: {
          margin: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
        content: {
          borderRadius: '20px',
          margin: 'auto',
          width: '750px',
          height: '350px',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f3f4f8',
        },
      }}
    >
      <BenefitFormTitle>
        <FormTitle>{t('benefits.versionManagement.edit.title')}</FormTitle>
        <div>
          <CloseIcon src={closeSvg} onClick={handleModal} />
        </div>
      </BenefitFormTitle>
      <BenefitFormContent>
        {selectedBenefitVersion && <BenefitVersionForm bVersion={selectedBenefitVersion} />}
      </BenefitFormContent>
    </ReactModal>
  );
};
